import React from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from '../icons';
import { cn } from '@/lib/utils';

// Variantes de tamanho disponíveis
type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';

interface ModalProps {
    /** Controla a visibilidade do modal */
    isOpen: boolean;
    /** Callback chamado ao fechar o modal */
    onClose: () => void;
    /** Conteúdo do modal */
    children: React.ReactNode;
    /** Tamanho do modal (default: 'md') */
    size?: ModalSize;
    /** Se true, não fecha ao clicar no backdrop */
    preventBackdropClose?: boolean;
    /** Classe adicional para o container do modal */
    className?: string;
    /** Classe adicional para o backdrop (ex.: bottom sheet no mobile) */
    overlayClassName?: string;
}

// Mapeamento de tamanhos para classes Tailwind
const sizeClasses: Record<ModalSize, string> = {
    xs: 'max-w-xs',      // 320px
    sm: 'max-w-sm',      // 384px
    md: 'max-w-md',      // 448px
    lg: 'max-w-lg',      // 512px
    xl: 'max-w-xl',      // 576px
    '2xl': 'max-w-2xl',  // 672px
    full: 'max-w-4xl',   // 896px
};

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    size = 'md',
    preventBackdropClose = false,
    className = '',
    overlayClassName = '',
}) => {
    if (!isOpen) return null;

    const handleBackdropClick = () => {
        if (!preventBackdropClose) {
            onClose();
        }
    };

    const modalContent = (
        <div
            className={cn(
                'fixed inset-0 z-[100] flex items-end justify-center overlay-premium p-0 pb-[env(safe-area-inset-bottom)] sm:items-center sm:p-s4 sm:pb-s4 md:p-s6',
                overlayClassName,
            )}
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={cn(
                    'flex max-h-[100dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-subtle bg-card shadow-modal sm:max-h-[90vh] sm:rounded-2xl',
                    sizeClasses[size],
                    className,
                )}
                onClick={(e) => e.stopPropagation()}
            >
                {children}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

// ============================================
// Modal Header
// ============================================
interface ModalHeaderProps {
    /** Título do modal */
    children: React.ReactNode;
    /** Callback para fechar o modal */
    onClose: () => void;
    /** Mostrar botão de fechar (default: true) */
    showCloseButton?: boolean;
    /** Classe adicional */
    className?: string;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
    children,
    onClose,
    showCloseButton = true,
    className = '',
}) => (
    <header
        className={cn(
            'flex min-h-11 shrink-0 items-center justify-between gap-s3 border-b border-subtle px-s4 py-s3 sm:px-s5 sm:py-s4',
            className,
        )}
    >
        <h2 className="min-w-0 text-balance text-lg font-semibold tracking-tight text-default">
            {children}
        </h2>
        {showCloseButton ? (
            <button
                type="button"
                onClick={onClose}
                className="toolbar-icon-btn inline-flex size-11 shrink-0 touch-manipulation items-center justify-center rounded-lg sm:size-9"
                aria-label="Fechar"
            >
                <XIcon className="size-5 shrink-0" aria-hidden />
            </button>
        ) : null}
    </header>
);

// ============================================
// Modal Body
// ============================================
interface ModalBodyProps {
    /** Conteúdo do body */
    children: React.ReactNode;
    /** Classe adicional */
    className?: string;
    /** Se true, não aplica padding (default: false) */
    noPadding?: boolean;
}

const ModalBody: React.FC<ModalBodyProps> = ({
    children,
    className = '',
    noPadding = false,
}) => (
    <div className={`
        ${noPadding ? '' : 'px-s4 py-s4 sm:p-s5'}
        min-h-0 flex-1 space-y-s4 overflow-y-auto scrollbar-premium
        ${className}
    `.trim().replace(/\s+/g, ' ')}>
        {children}
    </div>
);

// ============================================
// Modal Footer
// ============================================
interface ModalFooterProps {
    /** Conteúdo do footer (geralmente botões) */
    children: React.ReactNode;
    /** Classe adicional */
    className?: string;
}

const ModalFooter: React.FC<ModalFooterProps> = ({
    children,
    className = '',
}) => (
    <div className={`flex shrink-0 flex-col-reverse gap-s2 border-t border-subtle bg-surface-subtle px-s4 pt-s3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:justify-end sm:gap-s3 sm:px-s5 sm:py-s4 ${className}`}>
        {children}
    </div>
);

// Exporta Modal com subcomponentes anexados
export default Object.assign(Modal, {
    Header: ModalHeader,
    Body: ModalBody,
    Footer: ModalFooter,
});
